import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useSnackbar } from 'notistack';

import GenericForm from '../GenericForm';
import ProformaInvoiceEntity from 'src/db/entities/proforma_invoice/ProformaInvoiceEntity';
import { ProformaInvoiceDraft } from 'src/types/proformaInvoice';
import { cloneSettingsAsNewForInvoice, mapEntityToDraft } from '../mappers';
import { useAppDispatch, useAppStore } from 'src/app/hooks';
import { selectCurrentUserMemberData } from 'src/slices/workspacesSlice';
import { selectProposalSettings } from 'src/slices/proposalsSlice';
import {
  deleteProformaInvoice as deleteProformaInvoiceThunk,
  updateProformaInvoice as updateProformaInvoiceThunk,
} from 'src/slices/proformaInvoicesSlice/proformaInvoicesSlice';
import { MessageType } from 'components/UI/snackbar/providers/SnackBarProvider';
import DeleteDialog from 'components/UI/Dialogs/DeleteDialog';
import InvoicePdfLink from '../../InvoiceDrawer/InvoicePdfLink';
import InvoiceReviewBanner from '../../InvoiceDrawer/InvoiceReviewBanner';
import InvoicePayments from '../../InvoiceDrawer/InvoicePayments/InvoicePayments';
import { ProformaInvoiceFormValues } from '../types';

interface Props {
  invoice: ProformaInvoiceEntity;
  onSuccess?: () => void;
  onCancel: () => void;
  onFormDataChanged: () => void;
}

export default function UpdateForm({ invoice, onSuccess, onCancel, onFormDataChanged }: Props) {
  const { t } = useTranslation('proforma-invoices-page');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const state = store.getState();
  const currentUser = selectCurrentUserMemberData(state);
  const settingsFromStore = selectProposalSettings(state);

  const draft = useMemo<ProformaInvoiceDraft>(() => {
    const entityDraft = mapEntityToDraft(invoice);

    // Invoices created by the quote-signing flow may have no settings yet; the
    // backend copies them from the quote on the first save. Until then the
    // settings form starts from the workspace quote settings.
    return invoice.settings
      ? entityDraft
      : { ...entityDraft, settings: cloneSettingsAsNewForInvoice(settingsFromStore) };
  }, [invoice, settingsFromStore]);

  // Recording a payment changes the invoice (status, totals) while the form is open. Until
  // the user edits the form it follows the invoice. Once they have, the form keeps the
  // values it started from: a reset would discard their edits, and the status is sent only
  // when it differs from this baseline, so a status from before the payment is never
  // echoed back as a manual change.
  const latestDraftRef = useRef(draft);
  latestDraftRef.current = draft;
  const frozenDraftRef = useRef<ProformaInvoiceDraft | null>(null);
  const formDraft = frozenDraftRef.current ?? draft;

  const handleFormDataChanged = useCallback(() => {
    frozenDraftRef.current = frozenDraftRef.current ?? latestDraftRef.current;
    onFormDataChanged();
  }, [onFormDataChanged]);

  const handleSubmit = useCallback(
    async (values: ProformaInvoiceFormValues, initialValues: ProformaInvoiceFormValues) => {
      const nextDraft: ProformaInvoiceDraft = {
        ...values,
        uuid: invoice.uuid,
        updatedBy: currentUser?.email ?? invoice.updatedBy ?? null,
      };

      try {
        await dispatch(
          updateProformaInvoiceThunk({
            draft: nextDraft,
            initialDraft: { ...initialValues, uuid: invoice.uuid },
          })
        ).unwrap();

        enqueueSnackbar({
          message: t('form.notification.updated'),
          variant: MessageType.Success,
        });

        onSuccess?.();
      } catch (error) {
        enqueueSnackbar({
          message: t('common:message.server_error'),
          variant: MessageType.Error,
        });
      }
    },
    [dispatch, enqueueSnackbar, onSuccess, t, invoice, currentUser]
  );

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteProformaInvoiceThunk(invoice.uuid)).unwrap();

      enqueueSnackbar({
        message: t('form.notification.deleted'),
        variant: MessageType.Success,
      });

      onSuccess?.();
    } catch (error) {
      enqueueSnackbar({
        message: t('common:message.server_error'),
        variant: MessageType.Error,
      });
    }
  }, [dispatch, enqueueSnackbar, onSuccess, t, invoice.uuid]);

  return (
    <>
      <InvoiceReviewBanner invoice={invoice} onResolved={onSuccess} />
      <InvoicePdfLink uuid={invoice.uuid} />
      <InvoicePayments invoice={invoice} />
      <GenericForm
        defaultValues={formDraft}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        onDelete={() => setDeleteConfirmOpen(true)}
        onFormDataChanged={handleFormDataChanged}
        mode="update"
      />
      {deleteConfirmOpen && (
        <DeleteDialog
          title={t('form.deleteConfirm.title')}
          description={t('form.deleteConfirm.description')}
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          onSuccess={handleDelete}
          cancelButtonText={t('duplicates:Cancel_action')}
          deleteButtonText={t('duplicates:Delete_action')}
        />
      )}
    </>
  );
}
